/**
 * The values an office's own breakdown sheet shows, one per blank.
 *
 * An office format (OfficeBreakdownFormat on the backend) names, for each blank
 * on the office's paper sheet, a fact from breakdown_format_catalog.json. This
 * module answers those facts for one breakdown — from the very export model the
 * standard exports use (../exportModel.js), so a custom sheet can never say
 * something the standard one doesn't. Nothing here guesses: a fact the breakdown
 * doesn't answer is null, and the sheet leaves that blank empty.
 *
 * Keys (see the catalog's "about"):
 *   a named fact ............... "plan.group_number", "max.basic.pct"
 *   a procedure line's attribute  "line.<PROCEDURE_LINES key>.<attr>"
 *   a CDA code's attribute ...... "code.<5 digits>.<attr>" — the code's own row,
 *                                 else the line whose codes include it
 * A template field may list `fallbacks`: "(01204) Basic __%" reads
 * code.01204.pct, then max.basic.pct.
 *
 * Values: text → string; date → "YYYY-MM-DD"; money/number/percent/months →
 * number; yes_no → "yes" | "no"; enum → one of the catalog's values; null when
 * unknown. Formatting for paper and text is formatValue.js's job.
 *
 * Pure: inputs in (what loadExportInputs builds), plain values out.
 */
import dayjs from "dayjs";

import {
  BLANK, ageLimitOf, buildBreakdownExportModel, cycleMonths, feeGuide, frequencyWords, rowPct, yesNoToken,
} from "../exportModel";
import { PROCEDURE_LINES, ageOn, rowCode } from "../../../../lib/procedureFamilies";
import { planYearWord } from "../../../../lib/planDetails";
import CATALOG from "./breakdown_format_catalog.json";

export { CATALOG };

const LINES_BY_KEY = {};
PROCEDURE_LINES.forEach((line) => { LINES_BY_KEY[line.key] = line; });

// ---- small readers ---------------------------------------------------------------

function present(v) {
  return v !== null && v !== undefined && v !== "" && v !== BLANK;
}

function isoDate(value) {
  if (!present(value)) return null;
  const raw = String(value);
  // "Apr 1, 2027" (an already formatted reset date) as well as ISO strings.
  const d = /^\d{4}-\d{2}-\d{2}/.test(raw) ? dayjs(raw.split("T")[0]) : dayjs(new Date(raw));
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

function num(value) {
  if (!present(value)) return null;
  const n = Number(String(value).replace(/[$,%\s]/g, ""));
  return isFinite(n) ? n : null;
}

function whole(value) {
  if (!present(value)) return null;
  const m = /(\d{1,3})/.exec(String(value));
  return m ? Number(m[1]) : null;
}

function personName(first, last) {
  return [first, last].filter(Boolean).join(" ").trim() || null;
}

/** "yes" / "no" / null from a stored choice ("yes" | "no" | "unknown" | ""). */
function answer(value) {
  const v = present(value) ? String(value).toLowerCase() : "";
  return v === "yes" || v === "no" ? v : null;
}

function choice(value, allowed) {
  const v = present(value) ? String(value) : "";
  return allowed.indexOf(v) >= 0 ? v : null;
}

function yesNoOfToken(token) {
  return token === "Y" ? "yes" : token === "N" ? "no" : null;
}

// ---- the context: everything one breakdown can answer, built once ------------------

/** Build once per export; pass to resolveFact for each key. */
export function buildFactContext(inputs) {
  const safe = inputs || {};
  const model = buildBreakdownExportModel(safe);
  const answered = (safe.rows || []).filter((r) => r && !r.data_unavailable);
  const allRows = answered.concat((safe.unavailableRows || []).filter((r) => r && r.data_unavailable));
  const patient = safe.patient || {};
  return {
    inputs: safe,
    model,
    policy: safe.policy || {},
    categories: safe.categories || {},
    breakdown: safe.breakdown || {},
    answered,
    allRows,
    age: ageOn(patient.birth_date || patient.date_of_birth, safe.exportedAt),
    planYear: planYearWord(safe.policy),
  };
}

// ---- line and code attributes --------------------------------------------------------

function rowAttr(ctx, row, attr, family) {
  if (!row) return null;
  const unavailable = !!row.data_unavailable;
  switch (attr) {
    case "pct":
      return rowPct(row);
    case "covered":
      return unavailable ? null : yesNoOfToken(yesNoToken(row));
    case "frequency": {
      const words = frequencyWords(row, ctx.planYear);
      return present(words) ? words : null;
    }
    case "cycle_months":
      return cycleMonths(row);
    case "age_limit":
      return whole(family ? family.ageLimit : ageLimitOf(row));
    case "has_age_limit":
      // Only a stated limit is an answer; a note that doesn't mention one is not "no".
      return whole(family ? family.ageLimit : ageLimitOf(row)) != null ? "yes" : null;
    case "next_eligible":
      return isoDate(row.eligible_again_on);
    case "summary": {
      if (unavailable) return null;
      if (yesNoToken(row) === "N") return "not covered";
      const pct = rowPct(row);
      const words = frequencyWords(row, ctx.planYear);
      const parts = [pct != null ? `${pct}%` : null, present(words) ? words : null].filter(Boolean);
      return parts.length ? parts.join(", ") : null;
    }
    default:
      return null;
  }
}

function lineAttr(ctx, lineKey, attr) {
  const family = ctx.model.family[lineKey];
  if (!family || !family.row) return null;
  return rowAttr(ctx, family.row, attr, family);
}

/** The row for an exact CDA code: an answered row first, then an "unavailable" one. */
function codeRow(ctx, code) {
  const hit = (rows) => rows.find((r) => rowCode(r) === code) || null;
  return hit(ctx.answered) || hit(ctx.allRows);
}

/**
 * The line a code belongs to: the line whose own codes include it ("11107" is
 * polishing), else the line whose CDA range fits it most specifically ("42552"
 * falls in perio surgery's 42xxx; "23223" in composites on molars' 2322x rather
 * than restorative's 2xxxx). Null for a code no line covers ("41301").
 */
export function lineForCode(code) {
  const exact = PROCEDURE_LINES.find((line) => (line.codes || []).some((c) => c.code === code));
  if (exact) return exact;
  let best = null;
  let bestLength = 0;
  PROCEDURE_LINES.forEach((line) => {
    if ((line.excludePrefixes || []).some((p) => code.indexOf(p) === 0)) return;
    (line.prefixes || []).forEach((p) => {
      if (code.indexOf(p) === 0 && p.length > bestLength) {
        best = line;
        bestLength = p.length;
      }
    });
  });
  return best;
}

function codeAttr(ctx, code, attr) {
  const row = codeRow(ctx, code);
  if (row && !row.data_unavailable) return rowAttr(ctx, row, attr, null);
  const line = lineForCode(code);
  if (line) {
    const viaLine = lineAttr(ctx, line.key, attr);
    if (viaLine != null) return viaLine;
  }
  return row ? rowAttr(ctx, row, attr, null) : null;
}

// ---- named facts ---------------------------------------------------------------------

const CATEGORY = { preventative: "preventative", basic: "basic", major: "major", ortho: "orthodontics" };

function category(ctx, key) {
  return ctx.categories[CATEGORY[key]] || {};
}

function benefitPeriod(ctx) {
  const p = ctx.policy;
  const bp = p.benefit_period || {};
  return {
    start: isoDate(bp.start || p.benefit_year_start),
    end: isoDate(bp.end || p.benefit_year_end),
    resets: isoDate(bp.resets) || isoDate(ctx.inputs.maxResetDate),
  };
}

function periodType(ctx) {
  const stored = choice((ctx.policy.benefit_period || {}).type, ["calendar_year", "policy_year"])
    || choice(ctx.policy.benefit_period_type, ["calendar_year", "policy_year"]);
  if (stored) return stored;
  // The period's own start says it: Jan 1 is the calendar year, any other day a
  // policy (anniversary) year — the same reading the ProviderConnect reader uses.
  const start = benefitPeriod(ctx).start;
  if (!start) return null;
  return start.slice(5) === "01-01" ? "calendar_year" : "policy_year";
}

function planMax(ctx) {
  const basic = num(category(ctx, "basic").overall_max);
  if (basic != null) return basic;
  // A maximum the FTC wrote in the plan note ("Combined maximum: $1,500").
  const fromNote = ctx.model.maxima.basic;
  return fromNote && fromNote.fromNote ? num(fromNote.max) : null;
}

function orthoAges(p) {
  const lo = whole(p.ortho_age_min);
  const hi = whole(p.ortho_age_max);
  if (lo != null && hi != null) return `ages ${lo}–${hi}`;
  if (hi != null) return `to age ${hi}`;
  if (lo != null) return `from age ${lo}`;
  return null;
}

const NAMED = {
  "meta.today": (ctx) => isoDate(ctx.inputs.exportedAt) || dayjs().format("YYYY-MM-DD"),
  "meta.breakdown_date": (ctx) => isoDate(ctx.breakdown.upload_date || ctx.breakdown.created_at),
  "meta.source": (ctx) => ctx.model.provenance.sourceLabel || null,

  "patient.name": (ctx) => personName((ctx.inputs.patient || {}).first_name, (ctx.inputs.patient || {}).last_name),
  "patient.dob": (ctx) => isoDate((ctx.inputs.patient || {}).birth_date || (ctx.inputs.patient || {}).date_of_birth),
  "member.name": (ctx) => ctx.model.member.name || null,
  "member.dob": (ctx) => isoDate(ctx.policy.date_of_birth),
  "member.relation": (ctx) => ctx.model.member.relation || null,

  "carrier.name": (ctx) => ctx.model.carrier.name || null,
  "carrier.phone": (ctx) => ctx.model.carrier.phone || null,
  "carrier.edi": () => null,

  "plan.group_number": (ctx) => ctx.model.plan.groupNumber || null,
  "plan.member_id": (ctx) => ctx.model.plan.memberId || null,
  "plan.plan_name": (ctx) => ctx.model.plan.planName || null,
  "plan.fee_guide": (ctx) => ctx.model.plan.feeGuide || null,
  "plan.fee_guide_year": (ctx) => feeGuide(ctx.policy.fee_guide, ctx.inputs.exportedAt).year || null,
  "plan.deductible": (ctx) => ctx.model.plan.deductible || null,
  "plan.benefit_year": (ctx) => {
    const { benefitYearStart: start, benefitYearEnd: end } = ctx.model.plan;
    return start && end ? `${start} – ${end}` : null;
  },
  "plan.benefit_year_start": (ctx) => benefitPeriod(ctx).start,
  "plan.benefit_year_end": (ctx) => benefitPeriod(ctx).end,
  "plan.renews": (ctx) => benefitPeriod(ctx).resets,
  "plan.benefit_period_type": periodType,
  "plan.coverage_scope": (ctx) => choice(ctx.policy.coverage_scope, ["single", "family"]),
  "plan.max_scope": (ctx) => choice(ctx.policy.max_scope, ["per_person", "per_family"]),
  "plan.member_age_limit": (ctx) => whole(ctx.model.plan.memberAge),
  "plan.dependent_age": (ctx) => whole(ctx.model.plan.depChildAge),
  "plan.student_age": (ctx) => whole(ctx.model.plan.studentAge),
  "plan.specialist_fees": (ctx) => yesNoOfToken(ctx.model.plan.specialistFee.token),
  "plan.assignment": (ctx) => answer(ctx.policy.assignment_of_benefits),
  "plan.predetermination_required": (ctx) => (num(ctx.policy.predetermination_threshold) != null ? "yes" : null),
  "plan.predetermination_threshold": (ctx) => num(ctx.policy.predetermination_threshold),
  "plan.implants": (ctx) => {
    const stored = choice(ctx.policy.implants_coverage, ["covered", "not_covered", "estimate_required"]);
    if (stored) return stored === "not_covered" ? "no" : "yes";
    return lineAttr(ctx, "implants", "covered");
  },
  "plan.implants_pct": (ctx) => num(ctx.policy.implants_percentage) != null
    ? num(ctx.policy.implants_percentage) : lineAttr(ctx, "implants", "pct"),
  "plan.alternate_benefit": (ctx) => answer(ctx.policy.alternate_benefit),
  "plan.missing_tooth_clause": (ctx) => answer(ctx.policy.missing_tooth_clause),
  "plan.perio_endo_in_basic": (ctx) => answer(ctx.policy.perio_endo_in_basic),
  "plan.ortho_ages": (ctx) => orthoAges(ctx.policy),
  "plan.ortho_age_max": (ctx) => whole(ctx.policy.ortho_age_max),
  "plan.coverage_end_date": (ctx) => isoDate(ctx.policy.coverage_end_date),
  "plan.waiting_period": (ctx) => (present(ctx.policy.waiting_period_note) ? String(ctx.policy.waiting_period_note) : null),

  "max.plan": planMax,
  "max.combined": (ctx) => (ctx.model.maxima.combined === true ? "yes" : ctx.model.maxima.combined === false ? "no" : null),
  "max.ortho.is_lifetime": (ctx) => {
    const ortho = category(ctx, "ortho");
    if (ctx.model.maxima.ortho.lifetime) return "yes";
    return ortho.period === "annual" ? "no" : null;
  },
  "family.members": () => null,
};

Object.keys(CATEGORY).forEach((key) => {
  NAMED[`max.${key}.pct`] = (ctx) => num(category(ctx, key).coverage);
  NAMED[`max.${key}.max`] = (ctx) => num(category(ctx, key).overall_max);
  NAMED[`max.${key}.remaining`] = (ctx) => num(category(ctx, key).benefits_remaining);
});

// ---- public --------------------------------------------------------------------------

/** "fact" | "line" | "code" with its parts, or null for a key that names nothing. */
export function parseFactKey(key) {
  const k = String(key || "").trim();
  if (Object.prototype.hasOwnProperty.call(NAMED, k)) return { kind: "fact", key: k };
  const parts = k.split(".");
  if (parts.length !== 3) return null;
  const attrs = CATALOG.line_attrs.map((a) => a.attr);
  if (attrs.indexOf(parts[2]) < 0) return null;
  if (parts[0] === "line" && LINES_BY_KEY[parts[1]]) return { kind: "line", line: parts[1], attr: parts[2] };
  if (parts[0] === "code" && /^\d{5}$/.test(parts[1])) return { kind: "code", code: parts[1], attr: parts[2] };
  return null;
}

/** One fact's value for the breakdown in `ctx` (buildFactContext); null when unknown. */
export function resolveFact(key, ctx) {
  const parsed = parseFactKey(key);
  if (!parsed) return null;
  let value = null;
  if (parsed.kind === "fact") value = NAMED[parsed.key](ctx);
  else if (parsed.kind === "line") value = lineAttr(ctx, parsed.line, parsed.attr);
  else value = codeAttr(ctx, parsed.code, parsed.attr);
  return value === undefined || value === "" ? null : value;
}

/** A field's value: its fact, else each fallback in turn. */
export function resolveField(field, ctx) {
  const keys = [field && field.fact].concat((field && field.fallbacks) || []).filter(Boolean);
  for (let i = 0; i < keys.length; i += 1) {
    const value = resolveFact(keys[i], ctx);
    if (value != null) return value;
  }
  return null;
}

/** { values: { [field.id]: value }, model } — the model is the export model
 * the values came from (the text export signs off with its provenance). */
export function resolveTemplate(template, inputs) {
  const ctx = buildFactContext(inputs);
  const values = {};
  ((template && template.fields) || []).forEach((field) => {
    values[field.id] = resolveField(field, ctx);
  });
  return { values, model: ctx.model };
}

/** { [field.id]: value } for every field of a template. */
export function resolveFacts(template, inputs) {
  return resolveTemplate(template, inputs).values;
}

/** The type ("text", "percent", "yes_no"...) of the fact a key names, or null. */
export function factType(key) {
  const parsed = parseFactKey(key);
  if (!parsed) return null;
  if (parsed.kind === "fact") {
    const fact = CATALOG.facts.find((f) => f.key === parsed.key);
    return fact ? fact.type : null;
  }
  const attr = CATALOG.line_attrs.find((a) => a.attr === parsed.attr);
  return attr ? attr.type : null;
}
